import { format, parse } from "date-fns";

/**
 * Converts a Date object to a 12-hour time string format (e.g., "09:00 AM")
 */
export const dateToTimeString = (date: Date): string => {
  // Check if date is valid before formatting
  if (!date || isNaN(date.getTime())) {
    return "";
  }
  return format(date, "hh:mm aa");
};

/**
 * Converts a 12-hour time string (e.g., "09:00 AM", "9:00 AM", "09:00am") to a Date object
 * The date part will be set to today's date
 */
export const timeStringToDate = (timeString: string): Date => {
  // Normalize: trim, uppercase, and ensure space before AM/PM
  let normalized = timeString.trim().toUpperCase();
  // Add space before AM/PM if missing (e.g., "12:30AM" -> "12:30 AM")
  normalized = normalized.replace(/(\d)(AM|PM)$/i, "$1 $2");

  // Try parsing with padded hour first (09:00 AM)
  let result = parse(normalized, "hh:mm aa", new Date());
  if (!isNaN(result.getTime())) {
    return result;
  }

  // Try parsing with non-padded hour (9:00 AM)
  result = parse(normalized, "h:mm aa", new Date());
  if (!isNaN(result.getTime())) {
    return result;
  }

  // Return current time if parsing fails
  return new Date();
};

/**
 * Converts a 24-hour time string (e.g., "09:00") to a 12-hour format (e.g., "09:00 AM")
 */
export const time24To12 = (time24: string): string => {
  const [hours, minutes] = time24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 || 12;
  return `${hours12.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
};

/**
 * Converts a 12-hour time string (e.g., "09:00 AM") to 24-hour format (e.g., "09:00")
 */
export const time12To24 = (time12: string): string => {
  const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return "00:00";

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();

  if (period === "AM" && hours === 12) {
    hours = 0;
  } else if (period === "PM" && hours !== 12) {
    hours += 12;
  }

  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};
